import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Avatar,
  CircularProgress,
  Fade,
  Divider,
  InputAdornment,
  IconButton,
  useMediaQuery,
  Collapse,
  Alert
} from '@mui/material';
import {
  LockOpen,
  Visibility,
  VisibilityOff,
  Fingerprint,
  Login as LoginIcon,
  RocketLaunch,
  Terminal,
  CheckCircle,
  Security
} from '@mui/icons-material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import useAnalytics from '../../hooks/useAnalytics';

/* global PublicKeyCredential */

// Quantum Design System Components
const QuantumCard = styled('div')(({ theme }) => ({
  background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.92)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
  backdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: `
    0 8px 32px 0 ${alpha(theme.palette.primary.main, 0.18)},
    inset 0 0 12px ${alpha(theme.palette.primary.light, 0.1)}
  `,
  borderRadius: '28px',
  overflow: 'hidden',
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '6px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    animation: 'rainbow 8s linear infinite',
    backgroundSize: '400% 400%'
  },
  '@keyframes rainbow': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' }
  }
}));

const HolographicButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.85)} 0%, ${alpha(theme.palette.secondary.main, 0.7)} 100%)`,
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  boxShadow: `
    0 4px 30px ${alpha(theme.palette.primary.main, 0.25)},
    inset 0 2px 1px ${alpha(theme.palette.common.white, 0.2)}
  `,
  color: theme.palette.common.white,
  borderRadius: '18px',
  padding: '14px 36px',
  fontSize: '1.05rem',
  fontWeight: 600,
  letterSpacing: '0.06em',
  transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
  textTransform: 'none',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: `
      0 12px 45px ${alpha(theme.palette.primary.main, 0.4)},
      inset 0 2px 1px ${alpha(theme.palette.common.white, 0.3)}
    `,
    background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.95)} 0%, ${alpha(theme.palette.secondary.main, 0.8)} 100%)`,
  },
  '&.Mui-disabled': {
    background: `linear-gradient(145deg, ${alpha(theme.palette.action.disabledBackground, 0.8)} 0%, ${alpha(theme.palette.action.disabledBackground, 0.6)} 100%)`,
    color: alpha(theme.palette.text.disabled, 0.8)
  }
}));

const AnimatedInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.25),
      transition: 'all 0.3s ease'
    },
    '&:hover fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.6),
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}`,
      borderWidth: '2px'
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.primary.main,
    fontWeight: 500
  }
}));

const LoginParticles = () => (
  <Box sx={{
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    zIndex: 0,
    pointerEvents: 'none'
  }}>
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        style={{
          position: 'absolute',
          background: 'rgba(255, 255, 255, 0.6)',
          borderRadius: '50%',
          width: `${Math.random() * 5 + 1}px`,
          height: `${Math.random() * 5 + 1}px`,
        }}
        animate={{
          x: [0, Math.random() * 400 - 200],
          y: [0, Math.random() * 400 - 200],
          opacity: [0.1, 0.8, 0.1],
        }}
        transition={{
          duration: Math.random() * 10 + 10,
          repeat: Infinity,
          repeatType: 'reverse'
        }}
      />
    ))}
  </Box>
);

export default function QuantumLogin() {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [state, setState] = useState({
    loading: false,
    showPassword: false,
    activeStep: 0,
    biometricAvailable: false,
    error: null,
    success: false,
    securityCheck: false
  });

  const { 
    login, 
    isAuthenticated, 
    user, 
    isSubscribed, // ✅ CRITICAL: Get real subscription status
    forceRefreshUser // ✅ ADDED: Force refresh after login
  } = useContext(AuthContext);
  
  const { enqueueSnackbar } = useSnackbar();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ✅ ENHANCED: Redirect with subscription status verification
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('🔍 Login Redirect - User Status:', {
        isAuthenticated,
        userId: user._id,
        isSubscribed, // ✅ Use real isSubscribed from AuthContext
        subscriptionStatus: user.subscription?.status,
        credits: user.credits
      });
      
      trackEvent('AutoRedirectToDashboard', { 
        from: 'login',
        isSubscribed: isSubscribed,
        subscriptionStatus: user.subscription?.status
      });
      
      navigate('/ROIAnalyzer', { replace: true });
    }
  }, [isAuthenticated, user, navigate, trackEvent, isSubscribed]);

  // Check for biometric support
  useEffect(() => {
    const checkBiometrics = async () => {
      try {
        if (window.PublicKeyCredential) {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setState(prev => ({ ...prev, biometricAvailable: available }));
          if (available) {
            trackEvent('BiometricAvailability', { status: 'available' });
          }
        }
      } catch (error) {
        console.debug('Biometric check failed:', error);
        trackEvent('BiometricAvailability', { status: 'unavailable', error: error.message });
      }
    };
    checkBiometrics();
  }, [trackEvent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
    if (state.error) setState(prev => ({ ...prev, error: null }));
  };

  // ✅ EXPERT-APPROVED: Enhanced login handler with proper subscription sync
  const handleSubmit = async (e) => {
    e.preventDefault();
    trackEvent('LoginInitiated', { method: 'password' });
    
    try {
      setState(prev => ({ ...prev, loading: true, activeStep: 1, error: null }));
      
      // ✅ CRITICAL: Call login from AuthContext with enhanced error handling
      const loginResult = await login(credentials);
      
      if (loginResult && loginResult.success) {
        console.log('✅ Login successful - User Data:', {
          userId: loginResult.user?._id,
          isSubscribed: loginResult.user?.isSubscribed, // ✅ Direct from login response
          subscriptionStatus: loginResult.user?.subscription?.status,
          credits: loginResult.user?.credits
        });
        
        // ✅ ENHANCED: Force immediate user data refresh to ensure sync
        if (forceRefreshUser) {
          console.log('🔄 Forcing user data refresh after login...');
          await forceRefreshUser();
        }
        
        // Success flow with enhanced status
        setState(prev => ({ ...prev, activeStep: 2 }));
        await new Promise(resolve => setTimeout(resolve, 1200));
        
        setState(prev => ({ 
          ...prev, 
          activeStep: 3,
          success: true,
          loading: false
        }));
        
        // ✅ ENHANCED: Show subscription status in success message
        const subscriptionStatus = loginResult.user?.isSubscribed ? 'Subscribed' : 'Free Account';
        enqueueSnackbar(`Login successful! (${subscriptionStatus})`, { 
          variant: 'success',
          autoHideDuration: 3000 
        });
        
        trackEvent('LoginSuccess', {
          method: 'password',
          isSubscribed: loginResult.user?.isSubscribed,
          subscriptionStatus: loginResult.user?.subscription?.status
        });
        
        // ✅ ENHANCED: Immediate redirect with confidence
        setTimeout(() => {
          navigate('/ROIAnalyzer', { replace: true });
        }, 500);
        
      } else {
        throw new Error(loginResult?.error || 'Login failed - no success response');
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      
      // ✅ ENHANCED: Better error categorization
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Invalid email or password.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many login attempts. Please try again later.';
      } else if (error.message?.includes('network')) {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setState(prev => ({ 
        ...prev, 
        error: errorMessage,
        loading: false,
        activeStep: 0 
      }));
      
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 5000 
      });
      
      trackEvent('LoginFailed', { 
        method: 'password',
        error: errorMessage,
        email: credentials.email 
      });
    }
  };

  // ✅ ENHANCED: Biometric login with better error handling
  const handleBiometricLogin = async () => {
    if (!credentials.email) {
      setState(prev => ({ 
        ...prev, 
        error: 'Please enter your email first' 
      }));
      return;
    }
    
    trackEvent('BiometricLoginInitiated', { email: credentials.email });
    
    try {
      setState(prev => ({ ...prev, loading: true, activeStep: 1 }));
      
      // WebAuthn implementation with enhanced error handling
      const options = {
        publicKey: {
          challenge: new Uint8Array(32),
          rp: { 
            id: window.location.hostname,
            name: "Quantum ROI Analyzer" 
          },
          user: {
            id: new Uint8Array(16),
            name: credentials.email,
            displayName: credentials.email.split('@')[0] // Use name part of email
          },
          pubKeyCredParams: [
            { type: "public-key", alg: -7 }, // ES256
            { type: "public-key", alg: -257 } // RS256
          ],
          timeout: 60000,
          attestation: "none", // Changed to "none" for better compatibility
          authenticatorSelection: {
            authenticatorAttachment: "platform",
            userVerification: "required"
          }
        }
      };
      
      const credential = await navigator.credentials.create(options);
      trackEvent('BiometricCredentialCreated', { 
        type: credential.type,
        email: credentials.email 
      });
      
      // Simulate login processing with biometrics
      await new Promise(resolve => setTimeout(resolve, 1500));
      setState(prev => ({ ...prev, activeStep: 2 }));
      
      // ✅ Use the main login function for consistency
      const loginResult = await login({ 
        email: credentials.email, 
        biometric: true,
        credentialId: credential.id
      });
      
      if (loginResult && loginResult.success) {
        trackEvent('BiometricLoginSuccess', {
          email: credentials.email,
          isSubscribed: loginResult.user?.isSubscribed
        });
        
        setState(prev => ({ 
          ...prev, 
          activeStep: 3,
          success: true,
          loading: false
        }));
        
        enqueueSnackbar('Biometric login successful!', { variant: 'success' });
        
        setTimeout(() => {
          navigate('/ROIAnalyzer', { replace: true });
        }, 500);
      } else {
        throw new Error('Biometric authentication failed');
      }
    } catch (error) {
      console.error('❌ Biometric login error:', error);
      
      let errorMessage = 'Biometric authentication failed';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Biometric authentication was cancelled';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Biometric authentication not supported on this device';
      }
      
      setState(prev => ({ 
        ...prev, 
        error: errorMessage,
        loading: false,
        activeStep: 0 
      }));
      
      trackEvent('BiometricLoginFailed', { 
        error: errorMessage,
        errorName: error.name,
        email: credentials.email 
      });
      
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 5000 
      });
    }
  };

  const renderStepContent = () => {
    switch (state.activeStep) {
      case 0: // Initial form
        return (
          <Fade in={state.activeStep === 0} mountOnEnter unmountOnExit>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Collapse in={!!state.error}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {state.error}
                </Alert>
              </Collapse>
              
              <AnimatedInput
                margin="normal"
                required
                fullWidth
                name="email"
                label="Quantum ID (Email)"
                type="email"
                autoComplete="email"
                value={credentials.email}
                onChange={handleChange}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton edge="start" disabled>
                        <Security color="primary" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <AnimatedInput
                margin="normal"
                required
                fullWidth
                name="password"
                label="Access Code"
                type={state.showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={credentials.password}
                onChange={handleChange}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton edge="start" disabled>
                        <LockOpen color="primary" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setState(prev => ({ 
                          ...prev, 
                          showPassword: !prev.showPassword 
                        }))}
                        edge="end"
                      >
                        {state.showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              {state.biometricAvailable && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Fingerprint />}
                  onClick={handleBiometricLogin}
                  disabled={state.loading || !credentials.email}
                  sx={{
                    mb: 2,
                    borderRadius: '14px',
                    py: 1.5,
                    borderWidth: '2px',
                    '&:hover': { borderWidth: '2px' }
                  }}
                >
                  Quantum Biometric Scan
                </Button>
              )}
              
              <HolographicButton
                type="submit"
                fullWidth
                size="large"
                disabled={state.loading || !credentials.email || !credentials.password}
                startIcon={state.loading ? null : <LoginIcon />}
                sx={{ height: 52, mb: 2 }}
              >
                {state.loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Initiate Quantum Login'
                )}
              </HolographicButton>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                <Link 
                  component={RouterLink} 
                  to="/forgot-password" 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { color: theme.palette.primary.main }
                  }}
                >
                  Lost Quantum Access?
                </Link>
              </Typography>
            </Box>
          </Fade>
        );
      
      case 1: // Authentication
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: 'center', padding: '40px 0' }}
          >
            <CircularProgress 
              size={80} 
              thickness={4} 
              color="primary" 
              sx={{ mb: 3 }}
            />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Quantum Authentication in Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Verifying your quantum signature across dimensions
            </Typography>
          </motion.div>
        );
      
      case 2: // Security Check
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: 'center', padding: '40px 0' }}
          >
            <Terminal 
              color="primary" 
              sx={{ 
                fontSize: 80, 
                mb: 3,
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 0.8 },
                  '50%': { opacity: 1 },
                  '100%': { opacity: 0.8 }
                }
              }} 
            />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Security Protocol Engaged
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Scanning for temporal anomalies
            </Typography>
          </motion.div>
        );
      
      case 3: // Success
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: 'center', padding: '40px 0' }}
          >
            <CheckCircle 
              color="success" 
              sx={{ 
                fontSize: 80, 
                mb: 3,
                animation: 'scaleIn 0.5s ease-out',
                '@keyframes scaleIn': {
                  '0%': { transform: 'scale(0.5)', opacity: 0 },
                  '100%': { transform: 'scale(1)', opacity: 1 }
                }
              }} 
            />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Quantum Access Granted!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Transporting to your dashboard
            </Typography>
          </motion.div>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Quantum Login | ROI Analyzer</title>
        <meta name="description" content="Secure quantum authentication portal for accessing your ROI analysis dashboard" />
      </Helmet>
      
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: `linear-gradient(
            135deg, 
            ${theme.palette.background.default} 0%, 
            ${alpha(theme.palette.primary.dark, 0.15)} 100%
          )`,
          p: isMobile ? 2 : 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <LoginParticles />
        
        {/* Animated background elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          opacity: 0.4,
          background: `
            radial-gradient(circle at 20% 30%, ${alpha(theme.palette.secondary.main, 0.15)} 0%, transparent 30%),
            radial-gradient(circle at 80% 70%, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 30%)
          `,
        }} />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          style={{ width: '100%', maxWidth: '520px', zIndex: 1 }}
        >
          <QuantumCard>
            <Box sx={{ 
              p: isMobile ? 3 : 4,
              textAlign: 'center',
              position: 'relative'
            }}>
              {/* Progress indicators */}
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'center',
                gap: 1.5,
                mb: 4
              }}>
                {[0, 1, 2, 3].map((step) => (
                  <Box
                    key={step}
                    sx={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      bgcolor: state.activeStep >= step ? 
                        theme.palette.primary.main : 
                        alpha(theme.palette.action.disabled, 0.2),
                      transition: 'all 0.4s ease',
                      boxShadow: state.activeStep >= step ? 
                        `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}` : 
                        'none'
                    }}
                  />
                ))}
              </Box>
              
              <Avatar sx={{ 
                m: '0 auto 20px',
                bgcolor: 'primary.main',
                width: 80,
                height: 80,
                boxShadow: `0 0 0 6px ${alpha(theme.palette.primary.main, 0.2)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'rotate(15deg) scale(1.1)',
                  boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}>
                <LockOpen sx={{ fontSize: 40 }} />
              </Avatar>
              
              <Typography component="h1" variant="h3" sx={{ 
                mb: 1,
                fontWeight: 800,
                background: `linear-gradient(
                  45deg, 
                  ${theme.palette.primary.main} 30%, 
                  ${theme.palette.secondary.main} 90%
                )`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '0.05em',
                lineHeight: 1.2
              }}>
                Quantum Authentication
              </Typography>
              
              <Typography variant="subtitle1" color="text.secondary" sx={{ 
                mb: 4,
                maxWidth: '80%',
                mx: 'auto'
              }}>
                {state.activeStep === 0 
                  ? "Verify your identity to access the ROI Analyzer" 
                  : state.activeStep === 1 
                    ? "Establishing secure quantum connection..." 
                    : state.activeStep === 2 
                      ? "Finalizing security protocols..." 
                      : "Access granted! Preparing your dashboard..."}
              </Typography>
              
              {renderStepContent()}
              
              {state.activeStep === 0 && (
                <Fade in={state.activeStep === 0}>
                  <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Divider sx={{ 
                      my: 2, 
                      opacity: 0.3,
                      borderColor: 'divider'
                    }} />
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Not part of the quantum network yet?
                    </Typography>
                    <Button
                      onClick={() => navigate('/register')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        fontSize: '1rem',
                        '&:hover': {
                          color: theme.palette.primary.dark
                        }
                      }}
                      startIcon={<RocketLaunch />}
                    >
                      Join Quantum Network
                    </Button>
                  </Box>
                </Fade>
              )}
            </Box>
          </QuantumCard>
        </motion.div>
      </Box>
    </>
  );
}